import { Injectable } from '@angular/core';
import { InFlightServiceMap, StoreModel } from '@models/store.model';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { DropdownListService } from '@services/dropdown-list.service';
import * as commonActions from '@store/actions/common.action';
import { catchError, EMPTY, filter, map, switchMap, withLatestFrom } from 'rxjs';

import { AirCraftDDL } from '@models/master-data.model';
import { CommonService } from '@services/common.service';
import { MasterDataService } from '@services/master-data.service';
import {
    selectAirCraft,
    selectCountryCodes,
    selectCountryDDL,
    selectInFlightService,
} from '@store/selectors/common.selector';
import { isEmpty } from 'lodash';

@Injectable()
export class MasterDataEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly _dropdownListService: DropdownListService,
        private readonly _commonService: CommonService,
        private readonly _masterDataService: MasterDataService,
        private readonly _store: Store<StoreModel>
    ) {}

    loadAirlineLabel$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(commonActions.loadAirlineLabelAction),
            switchMap(() => {
                return this._commonService.getAirlineLabels().pipe(
                    map((airlineLabels) => {
                        return commonActions.loadDLLAction({
                            dllName: 'airlineLabels',
                            data: airlineLabels,
                        });
                    }),
                    catchError(() => EMPTY)
                );
            })
        );
    });

    loadCountryDialCode$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(commonActions.loadAirlineLabelAction),
            withLatestFrom(this._store.select(selectCountryCodes)),
            filter(([_, countryDialCodes]) => countryDialCodes.length === 0),
            switchMap(() => {
                return this._commonService.getCountryDialCode().pipe(
                    map((codes) => {
                        const codeArr = Object.values(codes).toSorted((a, b) => {
                            return a.country.localeCompare(b.country);
                        });
                        return commonActions.loadCountryDialCode({
                            array: codeArr,
                            maps: codes,
                        });
                    }),
                    catchError(() => EMPTY)
                );
            })
        );
    });

    loadCountryDDL$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(commonActions.loadCountryDDLAction),
            withLatestFrom(this._store.select(selectCountryDDL)),
            filter(([_, countryDDL]) => countryDDL.length === 0),
            switchMap(() => {
                return this._dropdownListService.getCountryDDL().pipe(
                    map((countries) => {
                        return commonActions.loadDLLAction({
                            dllName: 'countryDDL',
                            data: countries,
                        });
                    }),
                    catchError(() => EMPTY)
                );
            })
        );
    });

    loadAirCraftDDL$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(commonActions.loadAirCraftDDLAction),
            withLatestFrom(this._store.select(selectAirCraft)),
            filter(([_, airCrafts]) => isEmpty(airCrafts)),
            switchMap(() => {
                return this._masterDataService.getAirCrafts().pipe(
                    map((airCrafts) => {
                        const data = airCrafts.reduce(
                            (acc: Record<string, AirCraftDDL>, airCraft) => {
                                return {
                                    ...acc,
                                    [airCraft.AirCraftCode]: airCraft,
                                };
                            },
                            {} as Record<string, AirCraftDDL>
                        );

                        return commonActions.loadDLLAction({
                            dllName: 'airCrafts',
                            data: data,
                        });
                    }),
                    catchError(() => EMPTY)
                );
            })
        );
    });

    loadInFlightService$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(commonActions.loadInFlightServiceAction),
            withLatestFrom(this._store.select(selectInFlightService)),
            filter(([_, services]) => isEmpty(services)),
            switchMap(() => {
                return this._masterDataService.getInFlightServiceMaps().pipe(
                    map((services) => {
                        const data = services.reduce(
                            (acc: Record<string, InFlightServiceMap>, service) => {
                                return {
                                    ...acc,
                                    [service.type]: service,
                                };
                            },
                            {} as Record<string, InFlightServiceMap>
                        );

                        return commonActions.loadDLLAction({
                            dllName: 'inFlightServices',
                            data: data,
                        });
                    }),
                    catchError(() => EMPTY)
                );
            })
        );
    });
}
